import {LeanDocument} from 'mongoose';
import {CatalogueManagerApi} from '@/classes/api/catalogue-manager.api';
import {WarehouseManagerAPI} from '@/classes/api/warehouse-manager.api.class';
import {ScheduledProductSeriesDocument} from '@/documentTypes/catalogue-manager/ScheduledProductSeries.document';
import {idOf} from './entityPicker.helper';

/**
 * Books are the products of a "Produktreihe" (ScheduledProductSeries, e.g. Eulenbande). The position of
 * a product within `series.items` is its book number. What a child owns is its product-order inventory
 * (fulfilled product orders to/from the child's profile, summed up by the warehouse-manager).
 */
export interface BookPosition {
    seriesName: string;
    /** 1-based book number within the series */
    number: number;
}

export interface OwnedBook {
    productId: string;
    name: string;
    count: number;
    position?: BookPosition;
}

let seriesPromise: Promise<Map<string, BookPosition> | null> | null = null;

/** productId -> position in its series; loaded once per session (null without read access) */
export function loadBookPositions(): Promise<Map<string, BookPosition> | null> {
    if (!seriesPromise) {
        seriesPromise = CatalogueManagerApi.scheduledProductSeries.find({
            skipPagination: true,
            fields: ['name', 'items'],
        } as any).then((resp) => {
            const positions = new Map<string, BookPosition>();
            for (const series of (resp.scheduledProductSeries || []) as LeanDocument<ScheduledProductSeriesDocument>[]) {
                (series.items || []).forEach((item, index) => {
                    for (const product of (item.products || []) as unknown[]) {
                        const productId = idOf(product);
                        const known = positions.get(productId);
                        // A product in several series keeps its highest position
                        if (!known || known.number < index + 1) {
                            positions.set(productId, {seriesName: series.name, number: index + 1});
                        }
                    }
                });
            }
            return positions;
        }).catch(() => {
            seriesPromise = null;
            return null;
        });
    }
    return seriesPromise;
}

/** Inventory of all given students in one request: refProfile -> owned products (count > 0) */
export async function loadInventories(studentIds: string[]): Promise<Map<string, OwnedBook[]> | null> {
    if (!studentIds.length) {
        return new Map();
    }
    try {
        const resp = await WarehouseManagerAPI.productOrder.getInventory({
            refProfile: studentIds,
            populate: {product: {fields: ['name']}},
        } as any);
        const byStudent = new Map<string, OwnedBook[]>();
        for (const entry of resp.items || []) {
            if (!entry.count || entry.count <= 0) {
                continue;
            }
            const studentId = idOf(entry.refProfile);
            const product = entry.product as unknown as { _id?: unknown, name?: string };
            const list = byStudent.get(studentId) || [];
            list.push({productId: idOf(product), name: product?.name || '', count: entry.count});
            byStudent.set(studentId, list);
        }
        return byStudent;
    } catch (e) {
        // No read access on product orders
        return null;
    }
}

const naturalCompare = new Intl.Collator('de', {numeric: true, sensitivity: 'base'}).compare;

/**
 * Books of a child, highest book first. Only products that belong to a product series count as books.
 * Without access to the series (positions === null) all owned articles are returned, sorted by name descending.
 */
export function ownedBooks(owned: OwnedBook[], positions: Map<string, BookPosition> | null): OwnedBook[] {
    if (!positions) {
        return [...owned].sort((a, b) => naturalCompare(b.name, a.name));
    }
    return owned
        .filter((entry) => positions.has(entry.productId))
        .map((entry) => ({...entry, position: positions.get(entry.productId)}))
        .sort((a, b) => {
            const byNumber = (b.position?.number || 0) - (a.position?.number || 0);
            return byNumber || naturalCompare(b.name, a.name);
        });
}

export function bookLabel(book: OwnedBook): string {
    const name = book.name || (book.position ? `${book.position.seriesName} ${book.position.number}` : 'Unbekanntes Produkt');
    return book.count > 1 ? `${name} (${book.count}×)` : name;
}
